import { type LootDrop, rollLoot } from '../../game/loot';
import type { ZoneOpts } from '../../game/netHooks';
import type { SimEvent } from '../../sim/events';
import type { HudView, InstanceEnv } from './env';
import { type PortalFx, portalFx } from './fx';
import { emptyDrop, mergeDrop, rewardsOf } from './rewards';
import { InstanceScript, type ScriptState } from './script';

export const STORY_DUNGEONS = ['crypt', 'tomb', 'keep'] as const;

export interface StoryState extends ScriptState {
  cleared: boolean;
}

/**
 * Story dungeon clears (D4). The boss is the layout pack tagged `boss`; every
 * peer sees its replicated death and clears the dungeon for its own
 * character: `<id>_cleared` (opens the hidden exit portal), quest events
 * (`interact clear_<id>` and `clear <id>`), an announcement and a dungeon
 * chest (a rich one on the first clear, a smaller one afterwards) shown on
 * the rewards screen together with the boss's own personal drop.
 * Deaths use the game's default rules (checkpoint / Phoenix Plume).
 */
export class StoryScript extends InstanceScript<StoryState> {
  private fx: PortalFx | null = null;
  private done = false;
  /** Recent personal drops (the boss's own loot is folded into the clear screen). */
  private loot: { drop: LootDrop; source: string }[] = [];

  constructor(env: InstanceEnv, opts: ZoneOpts) {
    super(env, opts, { seq: 0, cleared: false });
  }

  get flag(): string {
    return `${this.z.layout.id}_cleared`;
  }

  protected override init(): void {
    const p = this.env.progression;
    if (p?.char.flags[this.flag]) this.openExit();
    if (p) {
      const off = p.on('loot', (e) => {
        this.loot.push(e);
        if (this.loot.length > 20) this.loot.shift();
      });
      this.onDispose(off);
    }
  }

  protected override localEvent(ev: SimEvent): void {
    if (ev.type !== 'death' || this.done) return;
    const a = this.world.actors.get(ev.id);
    if (!a?.monster?.tags.includes('boss')) return;
    this.clear(a.name);
  }

  protected override changed(prev: StoryState, next: StoryState): void {
    if (next.cleared && !prev.cleared) this.openExit();
  }

  /** Clears the dungeon for the local character (once per visit). */
  clear(bossName: string): void {
    if (this.done) return;
    this.done = true;
    if (this.isHost) this.update({ cleared: true });
    const layout = this.z.layout;
    const p = this.env.progression;
    if (p) {
      const first = !p.char.flags[this.flag];
      p.setFlag(this.flag);
      p.questEvent({ type: 'interact', target: `clear_${layout.id}` });
      p.questEvent({ type: 'clear', target: layout.id });
      p.char.counters[`clear_${layout.id}`] = (p.char.counters[`clear_${layout.id}`] ?? 0) + 1;
      // The boss already dropped its personal loot; the clear adds the dungeon chest.
      const chest = rollLoot(first ? 'bandit_chest' : 'chest', { level: layout.levelRange[1], rng: p.rng });
      p.grantDrop(chest, `${layout.name} chest`);
      this.env.save();
      // Show the chest together with the boss's drop (granted by the game right after this event).
      this.later(0.3, () => {
        const shown = mergeDrop(emptyDrop(), chest);
        for (const l of this.loot) if (l.source === bossName) mergeDrop(shown, l.drop);
        this.env.rewards(rewardsOf(shown, layout.name, first ? 'Dungeon cleared — first clear' : 'Dungeon cleared'));
      });
    }
    this.openExit();
    this.later(1.6, () => this.env.announce(`${layout.name} cleared — the way out is open`, 'success'));
  }

  private openExit(): void {
    if (this.fx || !this.env.visual) return;
    const exit = this.z.layout.portals.find((pt) => pt.requires === this.flag);
    if (!exit) return;
    const fx = portalFx(this.z, exit, 0x9ad8ff, exit.radius + 0.2);
    this.fx = fx;
    this.onDispose(() => fx.dispose());
  }

  protected override present(dt: number): void {
    this.fx?.update(dt);
  }

  view(): HudView | null {
    return null;
  }
}
